import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import './securities-suspensions.scss';

import {
  securitiesSuspensions,
  historicSecuritiesSuspensions,
} from '../../../../api/api';
import { fromOADate, groupBy } from '../../../../assets/scripts/helpers';

// Assets
// ---------------------
import HeaderImage from '../../../../assets/images/securities-suspensions-img.png';

// Components
// ---------------------
import { PageHeader } from '../../../../components/page-header/page-header';
import { Accordion } from '../../../../components/accordion/accordion';
import { Table } from '../../../../components/table/table';
// import { RSSLink } from '../../../../components/rss-link/rss-link';
import { LoadingSpinner } from '../../../../components/loading-spinner/loading-spinner';

export const SecuritiesSuspensions = ({ ...props }) => {
  const headerText = `The following table details the securities suspensions. The full 'List of Tradable Securities' is available on the Participant Information page`;
  const headerImg = (
    <img
      src={HeaderImage}
      alt="Ground view looking up between parallel skyscrapers"
    />
  );

  // const rssPath = 'https://gset.gs.com/Sigmaxmtf/Api/Document/rssSecurities';

  const tableHeaders = [
    { key: 'changeType', value: 'Change Type' },
    { key: 'instrumentName', value: 'Instrument Name' },
    { key: 'isinCode', value: 'ISIN Code' },
    { key: 'classCode', value: 'Class Code' },
    { key: 'securityExchange', value: 'Security Exchange' },
    { key: 'umtfCode', value: 'UMTF Code' },
    { key: 'venueMic', value: 'Venue Mic' },
    { key: 'currency', value: 'Currency' },
    { key: 'effectiveDate', value: 'Effective Date' },
  ];

  const historicTableHeaders = [
    {
      key: 'commDateTime',
      value: 'Date and Time of the Publication / Communication',
    },
    { key: 'actionType', value: 'Action Type' },
    { key: 'reason', value: 'Reason for the Action' },
    { key: 'effectiveFrom', value: 'Effective From' },
    { key: 'effectiveTo', value: 'Effective To' },
    { key: 'onGoing', value: 'Ongoing' },
    { key: 'tradingVenueMics', value: 'Trading Venue(s)' },
    { key: 'issuerName', value: 'Issuer Name' },
    { key: 'issuerLei', value: 'Issuer' },
    { key: 'isin', value: 'Instrument Identifier' },
    { key: 'instrumentFullName', value: 'Instrument Full Name' },
    { key: 'relatedIsins', value: 'Related Derivatives' },
    { key: 'otherRelatedIsins', value: 'Other Related Instruments' },
    { key: 'venueMic', value: 'Venue MIC' },
    { key: 'comments', value: 'Comments' },
  ];

  // Get Data
  // -----
  const [isLoading, setIsLoading] = useState(true);
  const [query] = useSearchParams();
  const [data, setData] = useState([]);

  const getHistoric = query.get('historic') === '1' ? 1 : 0;
  const linkText = getHistoric
    ? 'View Securities Suspensions'
    : 'View Historic Securities Suspensions';
  const historic = (
    <a href={`?historic=${getHistoric ? 0 : 1}`} alt={linkText}>
      {linkText}
    </a>
  );

  const call = getHistoric
    ? historicSecuritiesSuspensions
    : securitiesSuspensions;
  useEffect(() => {
    call(
      {},
      {
        StartDate: moment().subtract(10, 'years').format('YYYY/MM/DD'),
        EndDate: moment().add(30, 'days').format('YYYY/MM/DD'),
      }
    ).then((response) => {
      let result = getHistoric
        ? response.data.securitiesSuspensions
        : response.data.securitiesSuspensionsGetNew;

      // Unique Formatting
      result.forEach((r) => {
        if (!getHistoric) {
          r.effectiveDate = moment(fromOADate(r.effectiveDate)).format(
            'D/M/YYYY'
          );
        } else {
          r.commDateTime = moment(fromOADate(r.effectiveFrom)).format(
            'D/M/YYYY HH:mm'
          );
          r.effectiveFrom = moment(fromOADate(r.effectiveFrom)).format(
            'D/M/YYYY HH:mm'
          );
          r.effectiveTo = moment(fromOADate(r.effectiveTo)).format(
            'D/M/YYYY HH:mm'
          );
        }
      });

      let orderedResults = groupBy(result, (d) => d.year);
      orderedResults.forEach((year) => {
        year.data = groupBy(year.data, (m) => m.month);
      });

      // Find & Expand Most Recent Year and Month
      // -----
      let currentYear = orderedResults.reduce(function (prev, current) {
        return prev.key > current.key ? prev : current;
      });
      let currentMonth = currentYear.data.reduce(function (prev, current) {
        return prev.key > current.key ? prev : current;
      });
      // Get Current Variant
      // let currentYear = orderedResults.find((y) => {
      //   return y.key === moment().get('year');
      // });
      // let currentMonth = currentYear.data.find((m) => {
      //   return m.key === moment().get('month') + 1;
      // });

      if (currentYear) {
        currentYear.expanded = true;
      }
      if (currentMonth) {
        currentMonth.expanded = true;
      }
      // -----

      setData(orderedResults);
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`securities-suspensions-subpage`}>
      <PageHeader
        title={`${
          getHistoric
            ? 'Historic Securities Suspensions'
            : 'Securities Suspensions'
        }`}
        text={headerText}
        img={headerImg}
        link={historic}
      />
      <div className="content-wrapper">
        {/* <RSSLink path={rssPath} aria={`View RSS for ${headerTitle}`} /> */}
        <LoadingSpinner status={isLoading} name={'Securities Suspensions'} />
        <div className="content" hidden={isLoading}>
          {data.map((year, y) => {
            return (
              <Accordion
                key={`${y}-${year.key}`}
                text={year.key}
                initialExpand={year.expanded ? true : false}
              >
                {year.data.map((month, m) => {
                  return (
                    <Accordion
                      key={`${y}-${year.key}-${m}-${month.key}`}
                      text={moment()
                        .month(month.key - 1)
                        .format('MMMM')}
                      maxHeight={10000}
                      initialExpand={month.expanded ? true : false}
                    >
                      <div className="display-data">
                        <Table
                          headers={
                            getHistoric ? historicTableHeaders : tableHeaders
                          }
                          data={month.data}
                          aria={`Securities Suspensions ${year.key}, ${moment()
                            .month(month.key - 1)
                            .format('MMMM')}`}
                        />
                      </div>
                    </Accordion>
                  );
                })}
              </Accordion>
            );
          })}
          <div className="content-footer" />
        </div>
      </div>
    </div>
  );
};
